import { useState, useEffect } from "react";
import {
  Box, Typography, Select, MenuItem, IconButton, TextField,
  Button, useMediaQuery, useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";

// ─── Estilos base para botones de página ─────────────────────────────────────
const pageBtn = (active) => ({
  minWidth: 32, width: 32, height: 32,
  borderRadius: "8px",
  border: active ? "1.5px solid #6366f1" : "1.5px solid #e2e8f0",
  bgcolor: active ? "#6366f1" : "#fff",
  color: active ? "#fff" : "#64748b",
  fontWeight: active ? 700 : 500,
  fontSize: "0.8rem",
  cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  transition: "all 0.15s ease",
  "&:hover": active
    ? { bgcolor: "#4f46e5" }
    : { bgcolor: "#f8fafc", borderColor: "#a5b4fc", color: "#6366f1" },
  "&:disabled": { bgcolor: "#f1f5f9", color: "#cbd5e1", borderColor: "#e2e8f0", cursor: "not-allowed" },
});

export const Paginator = ({
  numPagina,
  setNumPagina,
  totalPages,
  filas,
  setFilas,
  totalDataItems,
}) => {
  const [irPagina, setIrPagina] = useState(numPagina);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => { setIrPagina(numPagina); }, [numPagina]);

  const handlePrev  = () => { if (numPagina > 1)          setNumPagina(numPagina - 1); };
  const handleNext  = () => { if (numPagina < totalPages)  setNumPagina(numPagina + 1); };
  const handleFirst = () => setNumPagina(1);
  const handleLast  = () => setNumPagina(totalPages);

  const handleIr = () => {
    const n = Number(irPagina);
    if (n >= 1 && n <= totalPages) setNumPagina(n);
    else setIrPagina(numPagina);
  };

  const handleRowsChange = (e) => {
    setFilas(Number(e.target.value));
    setNumPagina(1);
  };

  // ── Números de página a mostrar ──
  const renderPageNumbers = () => {
    const maxShow = isMobile ? 1 : 3;
    let start = Math.max(numPagina - Math.floor(maxShow / 2), 1);
    let end   = Math.min(start + maxShow - 1, totalPages);
    if (end - start < maxShow - 1) start = Math.max(end - maxShow + 1, 1);

    const items = [];

    if (!isMobile && numPagina > 3) {
      items.push(
        <Box key={1} component="button" onClick={() => setNumPagina(1)} sx={pageBtn(numPagina === 1)}>1</Box>
      );
    }
    if (!isMobile && start > 2) {
      items.push(<Typography key="s-ellipsis" sx={{ px: 0.5, color: "#94a3b8", fontSize: "0.85rem", alignSelf: "flex-end", pb: "2px" }}>…</Typography>);
    }

    for (let i = start; i <= end; i++) {
      if (i === numPagina) {
        items.push(
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <TextField
              type="number" size="small"
              value={irPagina}
              onChange={e => setIrPagina(Number(e.target.value))}
              onBlur={handleIr}
              onKeyDown={e => e.key === "Enter" && handleIr()}
              inputProps={{ min: 1, max: totalPages, style: { textAlign: "center", padding: "4px 6px", fontSize: "0.8rem", fontWeight: 700, color: "#6366f1" } }}
              sx={{
                width: 48,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", height: 32,
                  "& fieldset": { border: "1.5px solid #6366f1" },
                  "&:hover fieldset": { borderColor: "#4f46e5" },
                  "&.Mui-focused fieldset": { borderColor: "#4f46e5" },
                },
              }}
            />
            <Button
              onClick={handleIr} size="small"
              sx={{
                height: 32, minWidth: 0, px: 1.25,
                borderRadius: "8px", textTransform: "none",
                fontWeight: 600, fontSize: "0.75rem",
                bgcolor: "#6366f1", color: "#fff",
                "&:hover": { bgcolor: "#4f46e5" },
              }}
            >
              Ir
            </Button>
          </Box>
        );
      } else {
        items.push(
          <Box key={i} component="button" onClick={() => setNumPagina(i)} sx={pageBtn(false)}>{i}</Box>
        );
      }
    }

    if (!isMobile && end < totalPages - 1) {
      items.push(<Typography key="e-ellipsis" sx={{ px: 0.5, color: "#94a3b8", fontSize: "0.85rem", alignSelf: "flex-end", pb: "2px" }}>…</Typography>);
    }
    if (!isMobile && totalPages > 1 && numPagina < totalPages - 2) {
      items.push(
        <Box key={totalPages} component="button" onClick={() => setNumPagina(totalPages)} sx={pageBtn(numPagina === totalPages)}>
          {totalPages}
        </Box>
      );
    }

    return items;
  };

  // ── Botón de nav ──
  const NavBtn = ({ onClick, disabled, children }) => (
    <IconButton
      onClick={onClick} disabled={disabled} size="small"
      sx={{
        width: 32, height: 32, borderRadius: "8px",
        border: "1.5px solid #e2e8f0", bgcolor: "#fff",
        color: disabled ? "#cbd5e1" : "#64748b",
        transition: "all 0.15s",
        "&:hover:not(:disabled)": { bgcolor: "#f8fafc", borderColor: "#a5b4fc", color: "#6366f1" },
      }}
    >
      {children}
    </IconButton>
  );

  // ── Bloque items/selector (compartido) ──
  const ItemsSelector = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 500, whiteSpace: "nowrap" }}>
        Items:
      </Typography>
      <Select
        value={filas}
        onChange={handleRowsChange}
        size="small"
        sx={{
          height: 32, fontSize: "0.8rem", fontWeight: 600, color: "#1e293b",
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": { border: "1.5px solid #e2e8f0" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366f1" },
          "& .MuiSelect-select": { py: "4px", pr: "28px !important", pl: "10px" },
        }}
      >
        {[5, 10, 25, 50, 100].map(n => (
          <MenuItem key={n} value={n} sx={{ fontSize: "0.82rem" }}>{n}</MenuItem>
        ))}
      </Select>
    </Box>
  );

  // ── Bloque total (compartido) ──
  const TotalBadge = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 500 }}>Total:</Typography>
      <Box sx={{ bgcolor: "#f1f5f9", borderRadius: "6px", px: 1, py: 0.25 }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1e293b" }}>
          {totalDataItems?.toLocaleString("es-AR")}
        </Typography>
      </Box>
    </Box>
  );

  // ── Bloque navegación de páginas (compartido) ──
  const PageControls = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {!isMobile && <NavBtn onClick={handleFirst} disabled={numPagina === 1}><FirstPageIcon sx={{ fontSize: 16 }} /></NavBtn>}
      <NavBtn onClick={handlePrev} disabled={numPagina === 1}><ChevronLeftIcon sx={{ fontSize: 18 }} /></NavBtn>
      {renderPageNumbers()}
      <NavBtn onClick={handleNext} disabled={numPagina === totalPages}><ChevronRightIcon sx={{ fontSize: 18 }} /></NavBtn>
      {!isMobile && <NavBtn onClick={handleLast} disabled={numPagina === totalPages}><LastPageIcon sx={{ fontSize: 16 }} /></NavBtn>}
    </Box>
  );

  return (
    <Box sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5 }}>
      {isMobile ? (
        // ── MOBILE: fila 1 = Items + Total / fila 2 = navegación ──
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <ItemsSelector />
            <TotalBadge />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <PageControls />
          </Box>
        </Box>
      ) : (
        // ── DESKTOP: todo en una fila ──
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <ItemsSelector />
          <PageControls />
          <TotalBadge />
        </Box>
      )}
    </Box>
  );
};

export default Paginator;